import {
  cmsCompetitionReferences,
  cmsDocumentReferences,
  cmsImageReferences,
  cmsPublicContent,
} from '@shared/cms/content';
import type {
  SiteCmsCompetitionCard,
  SiteCmsDocumentResponse,
  SiteCmsDomainResponse,
  SiteCmsDraftResponse,
  SiteCmsImageResponse,
  SiteCmsPublicationState,
} from '@shared/types/entities';

import { cmsReviewKey } from './site-cms-workspace-model';

/** The review concerns selected public resources, not library order or unrelated uploads. */
export function cmsPublicationReview(
  draft: SiteCmsDraftResponse,
  domain: SiteCmsDomainResponse | undefined,
  state: SiteCmsPublicationState | undefined,
  images: readonly SiteCmsImageResponse[] | undefined,
  documents: readonly SiteCmsDocumentResponse[] | undefined,
  competitions: readonly SiteCmsCompetitionCard[] | undefined = [],
): string {
  const released = cmsPublicContent(draft.pages, draft.headerMenu);
  const imageIds = [...cmsImageReferences(released.pages, draft.branding)].sort();
  const competitionIds = cmsCompetitionReferences(released.pages).sort((a, b) => a - b);
  const slugs = [...cmsDocumentReferences(released.pages, released.headerMenu)].sort();
  return cmsReviewKey(draft, [
    domain?.hostname,
    domain?.verifiedAt,
    state?.requiresResave ?? false,
    state?.canPublish,
    imageIds.map(id => {
      const row = images?.find(image => image.id === id);
      return row ? [row.id, row.title, row.alt, row.updatedAt ?? row.createdAt] : [id, 'missing'];
    }),
    competitionIds.map(id => {
      const card = competitions?.find(item => item.id === id);
      return card ? [card.id, card.title, card.href, card.date, card.location] : [id, 'missing'];
    }),
    slugs.map(slug => {
      const row = documents?.find(doc => doc.slug === slug);
      return row ? [row.id, row.slug, row.title, row.updatedAt] : [slug, 'missing'];
    }),
  ]);
}

/** Local preflight failure: no publish POST was sent, unlike a transport timeout. */
export class CmsPublicationReviewError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CmsPublicationReviewError';
  }
}
